import { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { formatDuration } from '../growth';
import { GROWTH } from '../tokens';
import { SKILL_LABELS, SKILLS, type Cell, type GrowthHistoryYear, type GrowthPeriod, type GrowthResponse, type GrowthRubric, type SchemeLevel, type Skill } from '../types';
import { BandDot, CardHead, GROWTH_CARD_SX, HEADER_CELL_SX, SMALL_SX, STRONG_SX, TEXT_SX, colorOf, labelOf, scoreText } from './cardParts';

const BOX_WIDTH = 88;
const WINDOWS: Array<'boy' | 'moy' | 'eoy'> = ['boy', 'moy', 'eoy'];

function HistoryBox({ cell, scheme, current }: { cell: Cell; scheme: SchemeLevel[]; current: boolean }) {
  const base = { width: BOX_WIDTH, height: 32, flexShrink: 0, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: GROWTH.font, fontSize: '12px', lineHeight: '16px', fontWeight: 700, boxSizing: 'border-box', outline: current ? `2px solid ${GROWTH.action}` : 'none', outlineOffset: 1 } as const;
  if (cell.state === 'scored' && cell.band) {
    const label = cell.label || labelOf(scheme, cell.band);
    const score = scoreText(cell);
    return (
      <Box component="span" data-testid="history-cell" data-state="scored" title={label} aria-label={`${score} ${label}`.trim()} data-color={cell.color ?? colorOf(scheme, cell.band)} sx={{ ...base, bgcolor: cell.color ?? colorOf(scheme, cell.band), color: GROWTH.ink, fontVariantNumeric: 'tabular-nums' }}>
        {score}
      </Box>
    );
  }
  if (cell.state === 'unscorable') {
    return (
      <Box component="span" data-testid="history-cell" data-state="unscorable" sx={{ ...base, border: `1px solid ${GROWTH.divider}`, color: GROWTH.disabled }}>
        Unscorable
      </Box>
    );
  }
  if (cell.state === 'submitted') {
    return (
      <Box component="span" data-testid="history-cell" data-state="submitted" sx={{ ...base, bgcolor: GROWTH.pending, border: `1px solid ${GROWTH.divider}`, color: GROWTH.muted }}>
        Submitted
      </Box>
    );
  }
  return <Box component="span" data-testid="history-cell" data-state="none" aria-label="Not taken" sx={{ ...base, border: `1px dashed ${GROWTH.moveBorder.same}` }} />;
}

export function BenchmarkHistory({ history, scheme, period }: { history: GrowthHistoryYear[]; scheme: SchemeLevel[]; period: GrowthPeriod | null }) {
  const since = history[0]?.label ?? '';
  return (
    <Box component="section" data-testid="benchmark-history" sx={GROWTH_CARD_SX}>
      <CardHead title="Every benchmark, every skill" sub={since ? `Since BOY ${since}` : undefined} legend={scheme} />
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '16px', pb: '8px', borderBottom: `2px solid ${GROWTH.ink}` }}>
        <Box sx={{ width: 112, flexShrink: 0 }} />
        <Box sx={{ display: 'flex', gap: '32px' }}>
          {history.map((year) => (
            <Box key={year.schoolYear} data-testid="history-year" sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography component="span" sx={{ ...SMALL_SX, fontWeight: 700, color: GROWTH.ink }}>
                {year.label}
              </Typography>
              <Box sx={{ display: 'flex', gap: '4px' }}>
                {WINDOWS.map((window) => {
                  const current = period !== null && period.schoolYear === year.schoolYear && period.later === window;
                  return (
                    <Box key={window} component="span" sx={{ ...HEADER_CELL_SX, width: BOX_WIDTH, textAlign: 'center', color: current ? GROWTH.action : GROWTH.muted }}>
                      {window.toUpperCase()}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      {SKILLS.map((skill) => (
        <Box key={skill} data-testid="history-row" sx={{ display: 'flex', alignItems: 'center', gap: '16px', py: '8px' }}>
          <Typography component="span" sx={{ ...STRONG_SX, width: 112, flexShrink: 0 }}>
            {SKILL_LABELS[skill]}
          </Typography>
          <Box sx={{ display: 'flex', gap: '32px' }}>
            {history.map((year) => (
              <Box key={year.schoolYear} sx={{ display: 'flex', gap: '4px' }}>
                {WINDOWS.map((window) => (
                  <HistoryBox key={window} cell={year[skill][window]} scheme={scheme} current={period !== null && period.schoolYear === year.schoolYear && period.later === window} />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function formatScoredAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function scoredLine(entry: GrowthRubric): string {
  const when = entry.scoredAt ? formatScoredAt(entry.scoredAt) : '';
  if (entry.scoredBy && when) return `Scored by ${entry.scoredBy} · ${when}`;
  if (entry.scoredBy) return `Scored by ${entry.scoredBy}`;
  return when ? `Scored ${when}` : '';
}

function SkillSwitch({ value, onChange }: { value: Skill; onChange: (skill: Skill) => void }) {
  return (
    <Box role="group" aria-label="Skill" sx={{ display: 'flex', gap: '4px', p: '4px', bgcolor: GROWTH.surface, borderRadius: '8px' }}>
      {SKILLS.map((skill) => {
        const pressed = skill === value;
        return (
          <Box
            key={skill}
            component="button"
            type="button"
            aria-pressed={pressed}
            onClick={() => onChange(skill)}
            sx={{ fontFamily: GROWTH.font, fontSize: '12px', lineHeight: '24px', fontWeight: pressed ? 700 : 600, px: '12px', border: `1px solid ${pressed ? GROWTH.divider : 'transparent'}`, borderRadius: '4px', bgcolor: pressed ? GROWTH.cardBg : 'transparent', color: pressed ? GROWTH.ink : GROWTH.muted, cursor: 'pointer' }}
          >
            {SKILL_LABELS[skill]}
          </Box>
        );
      })}
    </Box>
  );
}

export function RubricIndicators({ rubric, scheme, period }: { rubric: Partial<Record<Skill, GrowthRubric>>; scheme: SchemeLevel[]; period: GrowthPeriod | null }) {
  const [skill, setSkill] = useState<Skill>('speaking');
  const entry = rubric[skill];
  const levels = [...scheme].sort((a, b) => a.score - b.score);
  const later = period?.later === 'eoy' ? 'EOY' : 'MOY';
  const label = period?.label ?? '';
  return (
    <Box component="section" data-testid="rubric-indicators" sx={GROWTH_CARD_SX}>
      <CardHead
        title="Rubric indicators"
        sub={`${later} ${label} against BOY ${label}`.trim()}
        right={<SkillSwitch value={skill} onChange={setSkill} />}
      />
      {!entry && (
        <Typography component="p" sx={{ ...TEXT_SX, color: GROWTH.muted, m: 0 }}>
          {`No ${later} ${SKILL_LABELS[skill]} score yet.`}
        </Typography>
      )}
      {entry && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', pb: '8px', borderBottom: `2px solid ${GROWTH.ink}` }}>
            <Box sx={{ ...HEADER_CELL_SX, width: 200, flexShrink: 0 }}>Indicator</Box>
            <Box sx={{ display: 'flex' }}>
              {levels.map((level) => (
                <Box key={level.label} component="span" sx={{ ...HEADER_CELL_SX, width: 80, textAlign: 'center' }}>
                  {level.label}
                </Box>
              ))}
            </Box>
            <Box sx={{ ...HEADER_CELL_SX, ml: 'auto' }}>Change</Box>
          </Box>
          {entry.indicators.map((indicator) => {
            const diff = indicator.was === null ? null : indicator.now - indicator.was;
            return (
              <Box key={indicator.name} data-testid="indicator-row" sx={{ display: 'flex', alignItems: 'center', gap: '16px', py: '12px', borderBottom: `1px solid ${GROWTH.divider}` }}>
                <Typography component="span" sx={{ ...STRONG_SX, width: 200, flexShrink: 0 }}>
                  {indicator.name}
                </Typography>
                <Box sx={{ display: 'flex' }}>
                  {levels.map((level) => {
                    const now = level.score === indicator.now;
                    const was = level.score === indicator.was;
                    return (
                      <Box key={level.label} sx={{ width: 80, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Box
                          component="i"
                          aria-hidden="true"
                          data-testid="indicator-dot"
                          data-level={level.label}
                          data-now={now ? 'true' : 'false'}
                          data-was={was ? 'true' : 'false'}
                          sx={{
                            display: 'block',
                            width: now || was ? 18 : 6,
                            height: now || was ? 18 : 6,
                            borderRadius: '9999px',
                            boxSizing: 'border-box',
                            bgcolor: now ? GROWTH.action : was ? GROWTH.cardBg : GROWTH.moveBorder.same,
                            border: was && !now ? `2px solid ${GROWTH.actionLight}` : 0,
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
                <Typography component="span" data-testid="indicator-change" sx={{ ...STRONG_SX, ml: 'auto', color: diff === null ? GROWTH.disabled : diff > 0 ? GROWTH.change.up : diff < 0 ? GROWTH.change.down : GROWTH.muted, fontWeight: diff === null || diff === 0 ? 500 : 700 }}>
                  {diff === null ? 'No BOY' : diff > 0 ? `▲ ${diff}` : diff < 0 ? `▼ ${-diff}` : 'Same'}
                </Typography>
              </Box>
            );
          })}
          {scoredLine(entry) && (
            <Typography component="p" sx={{ ...TEXT_SX, color: GROWTH.muted, pt: '16px', m: 0 }}>
              {scoredLine(entry)}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function responseDetail(response: GrowthResponse): string {
  if (response.skill === 'speaking') {
    if (response.durationSec === null) return '';
    const boy = response.boyDurationSec === null ? '' : ` · BOY ${formatDuration(response.boyDurationSec)}`;
    return `${formatDuration(response.durationSec)} recording${boy}`;
  }
  if (response.wordCount === null) return '';
  const boy = response.boyWordCount === null ? '' : ` · BOY ${response.boyWordCount}`;
  return `${response.wordCount} words${boy}`;
}

const LINK_SX = { ...STRONG_SX, color: GROWTH.action, display: 'inline-flex', alignItems: 'center', gap: '8px', textTransform: 'none', p: 0, minWidth: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } } as const;

export function Responses({ responses, scheme }: { responses: GrowthResponse[]; scheme: SchemeLevel[] }) {
  const [reading, setReading] = useState<number | null>(null);
  return (
    <Box component="section" data-testid="responses" sx={GROWTH_CARD_SX}>
      <CardHead title="Responses" />
      {responses.length === 0 && (
        <Typography component="p" sx={{ ...TEXT_SX, color: GROWTH.muted, m: 0 }}>
          No benchmark responses yet.
        </Typography>
      )}
      {responses.map((response, i) => (
        <Box key={`${response.window}-${response.skill}-${i}`} sx={{ borderBottom: `1px solid ${GROWTH.divider}`, '&:last-of-type': { borderBottom: 0 } }}>
          <Box data-testid="response-row" sx={{ display: 'flex', alignItems: 'center', gap: '16px', py: '12px' }}>
            <Typography component="span" sx={{ ...TEXT_SX, color: GROWTH.muted, width: 128, flexShrink: 0 }}>
              {response.window}
            </Typography>
            <Typography component="b" sx={{ ...STRONG_SX, width: 96, flexShrink: 0 }}>
              {SKILL_LABELS[response.skill]}
            </Typography>
            <Box sx={{ width: 128, flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px', ...TEXT_SX }}>
              {response.band > 0 && <BandDot scheme={scheme} band={response.band} color={response.color} />}
              {response.label || (response.band > 0 ? labelOf(scheme, response.band) : 'Unscorable')}
            </Box>
            <Typography component="span" sx={{ ...TEXT_SX, color: GROWTH.muted, flex: 1, minWidth: 0 }}>
              {responseDetail(response)}
            </Typography>
            {response.listenUrl && (
              <Button component="a" href={response.listenUrl} target="_blank" rel="noopener noreferrer" aria-label="Listen to response" disableRipple sx={LINK_SX}>
                <PlayArrowIcon sx={{ fontSize: 16 }} />
                Listen
              </Button>
            )}
            {response.text && (
              <Button type="button" aria-label={reading === i ? 'Hide response' : 'Read response'} aria-expanded={reading === i} disableRipple onClick={() => setReading(reading === i ? null : i)} sx={LINK_SX}>
                <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
                {reading === i ? 'Hide' : 'Read'}
              </Button>
            )}
          </Box>
          {reading === i && response.text && (
            <Box role="dialog" aria-label={`${SKILL_LABELS[response.skill]} response, ${response.window}`} data-testid="response-text" sx={{ mb: '12px', p: '16px', borderRadius: '8px', bgcolor: GROWTH.surface }}>
              <Typography sx={{ ...TEXT_SX, whiteSpace: 'pre-wrap', m: 0 }}>{response.text}</Typography>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
}
